// =============================================================================
// MeuDriver — Student Reviews
// Shows completed lessons pending a review + submitted reviews
// =============================================================================

import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Star,
  ArrowLeft,
  MessageSquare,
  ChevronDown,
  CheckCircle,
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { getInitials } from '@/lib/utils'

// ─── Types ────────────────────────────────────────────────────────────────────

interface CompletedBooking {
  id: string
  scheduled_at: string
  duration_minutes: number | null
  instructor: {
    id: string
    slug: string | null
    profile: { full_name: string | null; avatar_url: string | null } | null
  } | null
}

interface ReviewEntry {
  id: string
  rating: number
  comment: string | null
  created_at: string
  booking_id: string
  instructor: {
    slug: string | null
    profile: { full_name: string | null; avatar_url: string | null } | null
  } | null
}

// ─── Constants ────────────────────────────────────────────────────────────────

const REVIEW_TAGS = [
  'Muito paciente',
  'Pontual',
  'Boa didática',
  'Carro bem cuidado',
  'Recomendo',
  'Ótima comunicação',
  'Explicações claras',
  'Ambiente tranquilo',
]

const STAR_LABELS = ['', 'Ruim', 'Regular', 'Bom', 'Ótimo', 'Excelente!']

// ─── Star picker (interactive) ────────────────────────────────────────────────

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0)
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(star)}
            className="focus:outline-none transition-transform hover:scale-110 active:scale-95"
          >
            <Star
              className={`h-9 w-9 transition-colors duration-100 ${
                star <= (hovered || value)
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-gray-300 hover:text-gray-400'
              }`}
            />
          </button>
        ))}
      </div>
      <span className={`text-sm font-semibold transition-colors ${
        (hovered || value) > 0 ? 'text-amber-600' : 'text-transparent'
      }`}>
        {STAR_LABELS[hovered || value] || 'Selecione'}
      </span>
    </div>
  )
}

// ─── Stars (read-only) ────────────────────────────────────────────────────────

function Stars({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i < value ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`}
        />
      ))}
      <span className="ml-1.5 text-sm font-semibold text-amber-600">{value.toFixed(1)}</span>
    </div>
  )
}

// ─── Pending review card ───────────────────────────────────────────────────────

function PendingReviewCard({
  booking,
  onReviewed,
}: {
  booking: CompletedBooking
  onReviewed: (bookingId: string) => void
}) {
  const { user } = useAuth()
  const [expanded, setExpanded] = useState(false)
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  const instructorName = booking.instructor?.profile?.full_name ?? 'Instrutor'
  const avatarUrl = booking.instructor?.profile?.avatar_url
  const slug = booking.instructor?.slug
  const lessonDate = format(
    parseISO(booking.scheduled_at),
    "d 'de' MMMM 'de' yyyy",
    { locale: ptBR },
  )

  function toggleTag(tag: string) {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    )
  }

  async function handleSubmit() {
    if (rating === 0) {
      toast.error('Selecione uma avaliação de 1 a 5 estrelas.')
      return
    }
    if (!user?.id || !booking.instructor?.id) return
    setSubmitting(true)
    try {
      const { error } = await supabase.from('reviews').insert({
        booking_id: booking.id,
        student_id: user.id,
        instructor_id: booking.instructor.id,
        rating,
        comment: comment.trim() || null,
      })
      if (error) throw error
      toast.success('Avaliação enviada! Obrigado pelo feedback.')
      onReviewed(booking.id)
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Erro ao enviar avaliação.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="rounded-xl border border-amber-100 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-amber-50/40 transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <Link
          to={slug ? `/instrutores/${slug}` : '#'}
          className="shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Avatar className="h-11 w-11 ring-2 ring-amber-100">
            <AvatarImage src={avatarUrl ?? undefined} />
            <AvatarFallback className="bg-brand-100 text-brand-700 font-bold text-sm">
              {getInitials(instructorName)}
            </AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm truncate">{instructorName}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{lessonDate}</p>
        </div>
        <span className="shrink-0 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
          Avaliar
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200 ${
            expanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {expanded && (
        <div className="border-t border-amber-100 px-4 pb-5 pt-4 space-y-4 bg-amber-50/20">
          <StarPicker value={rating} onChange={setRating} />

          <div className="flex flex-wrap gap-2">
            {REVIEW_TAGS.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                  selectedTags.includes(tag)
                    ? 'bg-amber-400 border-amber-400 text-white font-medium shadow-sm'
                    : 'border-gray-200 text-gray-600 hover:border-amber-300 bg-white'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          <Textarea
            placeholder="Deixe um comentário sobre a aula... (opcional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={500}
            rows={3}
            className="text-sm resize-none bg-white"
          />

          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{comment.length}/500</span>
            <Button
              onClick={handleSubmit}
              disabled={rating === 0 || submitting}
              size="sm"
              className="bg-amber-500 hover:bg-amber-600 text-white gap-2 disabled:opacity-50"
            >
              {submitting ? 'Enviando...' : 'Enviar avaliação'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function StudentReviews() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: completedBookings, isLoading: loadingBookings } = useQuery<CompletedBooking[]>({
    queryKey: ['student-completed-bookings', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id, scheduled_at, duration_minutes,
          instructor:instructors!bookings_instructor_id_fkey(
            id, slug,
            profile:profiles(full_name, avatar_url)
          )
        `)
        .eq('student_id', user!.id)
        .eq('status', 'completed')
        .order('scheduled_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as unknown as CompletedBooking[]
    },
    staleTime: 1000 * 60,
  })

  const { data: reviews, isLoading: loadingReviews } = useQuery<ReviewEntry[]>({
    queryKey: ['student-reviews-given', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          id, rating, comment, created_at, booking_id,
          instructor:instructors!reviews_instructor_id_fkey(
            slug,
            profile:profiles(full_name, avatar_url)
          )
        `)
        .eq('student_id', user!.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as unknown as ReviewEntry[]
    },
    staleTime: 1000 * 60,
  })

  const isLoading = loadingBookings || loadingReviews

  const reviewedIds = new Set((reviews ?? []).map((r) => r.booking_id))
  const pendingBookings = (completedBookings ?? []).filter((b) => !reviewedIds.has(b.id))

  const avgRating =
    reviews && reviews.length > 0
      ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
      : null

  function handleReviewed() {
    queryClient.invalidateQueries({ queryKey: ['student-reviews-given', user?.id] })
    queryClient.invalidateQueries({ queryKey: ['student-completed-bookings', user?.id] })
  }

  const hasContent = pendingBookings.length > 0 || (reviews?.length ?? 0) > 0

  return (
    <div className="max-w-2xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild className="shrink-0">
          <Link to="/aluno/dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-400 fill-amber-400" />
            Minhas avaliações
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Avaliações que você deu a instrutores
          </p>
        </div>
        {avgRating !== null && (
          <div className="shrink-0 text-right">
            <p className="text-2xl font-bold text-amber-500">{avgRating.toFixed(1)}</p>
            <p className="text-xs text-muted-foreground">média geral</p>
          </div>
        )}
      </div>

      {/* Summary pills */}
      {!isLoading && hasContent && (
        <div className="flex items-center gap-2 flex-wrap">
          {pendingBookings.length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-medium text-amber-800">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              {pendingBookings.length} para avaliar
            </span>
          )}
          {(reviews?.length ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 border border-green-200 px-3 py-1 text-xs font-medium text-green-800">
              <CheckCircle className="h-3 w-3 text-green-500" />
              {reviews!.length} enviada{reviews!.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {/* Skeletons */}
      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl border border-gray-100 bg-white p-4 flex gap-3">
              <Skeleton className="h-11 w-11 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && (
        <>
          {/* Pending reviews */}
          {pendingBookings.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-white text-[10px] font-bold shrink-0">
                  {pendingBookings.length}
                </span>
                Para avaliar
              </h2>
              {pendingBookings.map((booking) => (
                <PendingReviewCard
                  key={booking.id}
                  booking={booking}
                  onReviewed={handleReviewed}
                />
              ))}
            </div>
          )}

          {/* Submitted reviews */}
          {(reviews?.length ?? 0) > 0 && (
            <div className="space-y-3">
              {pendingBookings.length > 0 && (
                <h2 className="text-sm font-semibold text-gray-700">Avaliações enviadas</h2>
              )}
              {reviews!.map((r) => {
                const instructorName = r.instructor?.profile?.full_name ?? 'Instrutor'
                const avatarUrl = r.instructor?.profile?.avatar_url
                const slug = r.instructor?.slug
                const date = format(
                  parseISO(r.created_at),
                  "d 'de' MMMM 'de' yyyy",
                  { locale: ptBR },
                )

                return (
                  <div
                    key={r.id}
                    className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden"
                  >
                    <div className="p-4 sm:p-5">
                      <div className="flex items-start gap-4">
                        <Link
                          to={slug ? `/instrutores/${slug}` : '#'}
                          className="shrink-0"
                        >
                          <Avatar className="h-11 w-11 ring-2 ring-amber-100">
                            <AvatarImage src={avatarUrl ?? undefined} />
                            <AvatarFallback className="bg-brand-100 text-brand-700 font-bold text-sm">
                              {getInitials(instructorName)}
                            </AvatarFallback>
                          </Avatar>
                        </Link>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div>
                              <Link
                                to={slug ? `/instrutores/${slug}` : '#'}
                                className="font-semibold text-gray-900 hover:text-brand-600 transition-colors text-sm"
                              >
                                {instructorName}
                              </Link>
                              <p className="text-xs text-muted-foreground mt-0.5">{date}</p>
                            </div>
                            <Stars value={r.rating} />
                          </div>
                          {r.comment ? (
                            <div className="mt-3 flex items-start gap-2 bg-gray-50 rounded-lg px-3 py-2.5">
                              <MessageSquare className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                              <p className="text-sm text-gray-700 italic">"{r.comment}"</p>
                            </div>
                          ) : (
                            <p className="mt-2 text-xs text-muted-foreground italic">
                              Sem comentário
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Empty state */}
          {!hasContent && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 mb-4">
                <Star className="h-7 w-7 text-amber-300" />
              </div>
              <p className="text-base font-semibold text-gray-700">Nenhuma avaliação ainda</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                Após concluir uma aula, você poderá avaliar o instrutor.
              </p>
              <Button asChild className="mt-4" variant="outline">
                <Link to="/instrutores">Encontrar instrutores</Link>
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
